import React, { useEffect, useRef, useState, useMemo } from 'react';
import PageHeader from './components/PageHeader';
import OrganizationPicker from './components/OrganizationPicker';

export default function UserProfilePage({ user, organization, onSave, onCancel, extraSection = null }: {
  user: any;
  organization?: any;
  onSave?: (updated: any) => void;
  onCancel?: () => void;
  // Rendered below the profile card (the MCA sign-ins panel for MCA users).
  extraSection?: React.ReactNode;
}) {
  const [form, setForm] = useState({
    id: user?.id,
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'viewer',
    org: user?.org || '',
    defaultOrganizationId: user?.defaultOrganizationId ?? null,
  });

  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [touched, setTouched] = useState({ name: false, email: false, current: false, newPass: false, confirm: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validate = (nextForm = form) => {
    const errors: Record<string, string> = {};
    const name = String(nextForm?.name || '').trim();
    const email = String(nextForm?.email || '').trim();

    if (!name) errors.name = 'Name is required.';
    if (!email) errors.email = 'Email is required.';
    else if (!/^\S+@\S+\.\S+$/.test(email)) errors.email = 'Please enter a valid email address.';

    const isChangingPassword = passwords.current || passwords.newPass || passwords.confirm;
    if (isChangingPassword) {
      if (!passwords.current) errors.current = 'Current password is required.';
      if (!passwords.newPass) errors.newPass = 'New password is required.';
      else if (passwords.newPass.length < 10) errors.newPass = 'Password must be at least 10 characters.';
      if (!passwords.confirm) errors.confirm = 'Please confirm your new password.';
      else if (passwords.newPass && passwords.confirm !== passwords.newPass) errors.confirm = 'Passwords do not match.';
    }

    return errors;
  };

  const errors = useMemo(() => validate(form), [form, passwords]);
  const isValid = Object.keys(errors).length === 0;
  const showNameError = (touched.name || submitAttempted) && Boolean(errors.name);
  const showEmailError = (touched.email || submitAttempted) && Boolean(errors.email);
  const showCurrentError = (touched.current || submitAttempted) && Boolean(errors.current);
  const showNewPassError = (touched.newPass || submitAttempted) && Boolean(errors.newPass);
  const showConfirmError = (touched.confirm || submitAttempted) && Boolean(errors.confirm);

  // On browser refresh, `user` can arrive after the first render.
  // Hydrate the form once data is available, but avoid clobbering in-progress edits.
  const hydratedUserIdRef = useRef<any>(undefined);
  const defaultOrganizationEditedRef = useRef(false);
  const profileFieldsEditedRef = useRef({ name: false, email: false });
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const changedAccount = hydratedUserIdRef.current !== undefined
      && hydratedUserIdRef.current !== userId;
    if (changedAccount) {
      profileFieldsEditedRef.current = { name: false, email: false };
      defaultOrganizationEditedRef.current = false;
      setTouched({ name: false, email: false, current: false, newPass: false, confirm: false });
      setPasswords({ current: '', newPass: '', confirm: '' });
      setSubmitAttempted(false);
    }

    // Preserve each edited field before blur while refreshing server-owned metadata
    // and fields that the user has not changed.
    const edited = { ...profileFieldsEditedRef.current };
    const organizationEdited = defaultOrganizationEditedRef.current;
    setForm((prev) => ({
      ...prev,
      id: userId,
      name: edited.name ? prev.name : user?.name || '',
      email: edited.email ? prev.email : user?.email || '',
      role: user?.role || 'viewer',
      org: organizationEdited ? prev.org : (user as any)?.org || (changedAccount ? '' : prev.org) || '',
      defaultOrganizationId: organizationEdited
        ? prev.defaultOrganizationId
        : (user as any)?.defaultOrganizationId ?? null,
    }));
    hydratedUserIdRef.current = userId;
  }, [user?.id, user?.name, user?.email, user?.role, (user as any)?.org, (user as any)?.defaultOrganizationId, organization?.id]);

  const update = (field: 'name' | 'email', value: string) => {
    profileFieldsEditedRef.current[field] = true;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setSubmitAttempted(true);
    if (!isValid) return;
    onSave?.({
      ...(passwords.current && passwords.newPass ? { currentPassword: passwords.current, newPassword: passwords.newPass } : {}),
      ...user,
      ...form,
      name: String(form.name || '').trim(),
      email: String(form.email || '').trim()
    });
  };

  return (
    <div className="stack" data-testid="user-profile-page">
      <PageHeader
        title="Profile"
        subtitle="Manage your account details"
      />

      <div className="card">
        <div className="form-grid">
          <div className="field">
            <label className="label">Full name</label>
            <input
              className={`input ${showNameError ? 'error' : ''}`}
              value={form?.name||''}
              onChange={(e) => update('name', e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
              placeholder="Your name"
              aria-invalid={showNameError}
              required
              data-testid="user-profile-name"
            />
            {showNameError && <div className="error-text" data-testid="user-profile-name-error">{errors.name}</div>}
          </div>

          <div className="field">
            <label className="label">Email</label>
            <input
              type="email"
              className={`input ${showEmailError ? 'error' : ''}`}
              value={form?.email||''}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
              placeholder="you@example.com"
              aria-invalid={showEmailError}
              required
              data-testid="user-profile-email"
            />
            {showEmailError && <div className="error-text" data-testid="user-profile-email-error">{errors.email}</div>}
          </div>

          <div className="field" style={{ gridColumnStart: 1 }}>
            <label className="label">Role ({organization?.name || form?.org || ''})</label>
            <input
              style={{textTransform: 'capitalize'}}
              className="input"
              value={form?.role||'viewer'}
              disabled
              readOnly
              data-testid="user-profile-role"
            />
          </div>

          <div className="field">
            <OrganizationPicker
              label="Default Organization"
              value={form.defaultOrganizationId}
              testId="user-profile-default-organization"
              onChange={(id, name) => {
                defaultOrganizationEditedRef.current = true;
                setForm((prev) => ({
                  ...prev,
                  defaultOrganizationId: id,
                  org: name,
                }));
              }}
            />
          </div>

          <div className="field" style={{ gridColumnStart: 1 }}>
            <label className="label">Current Password</label>
            <input
              type="password"
              className={`input ${showCurrentError ? 'error' : ''}`}
              value={passwords.current}
              onChange={(e) => setPasswords((prev) => ({ ...prev, current: e.target.value }))}
              onBlur={() => setTouched((prev) => ({ ...prev, current: true }))}
              placeholder="Current Password"
              aria-invalid={showCurrentError}
              autoComplete="current-password"
              data-testid="user-profile-current-password"
            />
            {showCurrentError && <div className="error-text" data-testid="user-profile-current-password-error">{errors.current}</div>}
          </div>

          <div className="field">
            <label className="label">New Password</label>
            <input
              type="password"
              className={`input ${showNewPassError ? 'error' : ''}`}
              value={passwords.newPass}
              onChange={(e) => setPasswords((prev) => ({ ...prev, newPass: e.target.value }))}
              onBlur={() => setTouched((prev) => ({ ...prev, newPass: true }))}
              placeholder="New Password"
              aria-invalid={showNewPassError}
              autoComplete="new-password"
              minLength={10}
              data-testid="user-profile-new-password"
            />
            {showNewPassError && <div className="error-text" data-testid="user-profile-new-password-error">{errors.newPass}</div>}
          </div>

          <div className="field" style={{ gridColumnStart: 1 }}>
            <label className="label">Confirm Password</label>
            <input
              type="password"
              className={`input ${showConfirmError ? 'error' : ''}`}
              value={passwords.confirm}
              onChange={(e) => setPasswords((prev) => ({ ...prev, confirm: e.target.value }))}
              onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
              placeholder="Confirm New Password"
              aria-invalid={showConfirmError}
              autoComplete="new-password"
              data-testid="user-profile-confirm-password"
            />
            {showConfirmError && <div className="error-text" data-testid="user-profile-confirm-password-error">{errors.confirm}</div>}
          </div>
        </div>

        <div className="card-actions" style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="button secondary" onClick={onCancel} data-testid="user-profile-cancel">Cancel</button>
          <button className="button" onClick={handleSave} disabled={submitAttempted && !isValid} data-testid="user-profile-save">Save Profile</button>
        </div>
      </div>

      {extraSection}
    </div>
  );
}
